import { useEffect, useState } from 'react';
import { Button, Text } from '../ui';
import { useToast } from '../Toast';
import { supabase } from '../../lib/supabase';

// Comms -> Templates. A read-only gallery of every email the platform sends,
// rendered server-side with sample data (so a preview is exactly what ships).
// The sign-in / account emails are sent by Supabase, not us: for those we also
// offer "Copy HTML for Supabase", which fetches the same template rendered with
// the GoTrue variables so the team can paste it into the Supabase dashboard.

interface TplItem {
  id: string;
  label: string;
  auth?: boolean;
  supabase?: string; // dashboard template name (auth items only)
}

const GROUPS: Array<{ group: string; note?: string; items: TplItem[] }> = [
  {
    group: 'Member emails',
    items: [
      { id: 'invite', label: 'Member invite' },
      { id: 'announcement', label: 'Bulk: announcement' },
      { id: 'invitation', label: 'Bulk: invitation' },
    ],
  },
  {
    group: 'Saleroom',
    items: [
      { id: 'outbid', label: 'Outbid' },
      { id: 'ending_soon', label: 'Ending soon' },
      { id: 'won', label: 'Won' },
      { id: 'lost', label: 'Lost' },
      { id: 'test', label: 'Test notification' },
    ],
  },
  {
    group: 'Sign-in & account',
    note: 'Sent by Supabase. Copy each into Supabase Dashboard -> Authentication -> Email Templates to brand them.',
    items: [
      { id: 'auth-magiclink', label: 'Magic link (sign in)', auth: true, supabase: 'Magic Link' },
      { id: 'auth-invite', label: 'Invite user', auth: true, supabase: 'Invite user' },
      { id: 'auth-signup', label: 'Confirm signup', auth: true, supabase: 'Confirm signup' },
      { id: 'auth-recovery', label: 'Reset password', auth: true, supabase: 'Reset Password' },
      { id: 'auth-email_change', label: 'Change email', auth: true, supabase: 'Change Email Address' },
    ],
  },
];

const ALL_ITEMS = GROUPS.flatMap((g) => g.items);

async function authToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

export function EmailTemplatesPanel({ active }: { active: boolean }) {
  const toast = useToast();
  const [selected, setSelected] = useState<string>('invite');
  const [subject, setSubject] = useState<string>('');
  const [html, setHtml] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [copying, setCopying] = useState(false);

  const current = ALL_ITEMS.find((i) => i.id === selected);

  async function loadPreview(id: string) {
    setLoading(true);
    setError(null);
    try {
      const token = await authToken();
      const res = await fetch(`/api/admin/email/preview?type=${encodeURIComponent(id)}`, {
        headers: { authorization: `Bearer ${token ?? ''}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e?.error ?? `Failed (${res.status})`);
      }
      const data = await res.json();
      setSubject(data.subject ?? '');
      setHtml(data.html ?? '');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load preview.');
      setHtml('');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (active) void loadPreview(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, selected]);

  async function copyForSupabase() {
    setCopying(true);
    try {
      const token = await authToken();
      const res = await fetch(`/api/admin/email/preview?type=${encodeURIComponent(selected)}&mode=supabase`, {
        headers: { authorization: `Bearer ${token ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const data = await res.json();
      await navigator.clipboard.writeText(data.html ?? '');
      toast.success(`Copied. Paste into Supabase -> Email Templates -> ${current?.supabase ?? 'the matching template'}.`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Copy failed.');
    } finally {
      setCopying(false);
    }
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Email templates</h2>
        <Button type="button" variant="ghost" size="sm" onClick={() => loadPreview(selected)} disabled={loading}>
          {loading ? '…' : 'Refresh'}
        </Button>
      </div>

      <Text role="small" muted className="admin__compose-intro">
        Every email the platform sends, rendered with sample data so it is exactly what members receive.
        The sign-in and account emails are sent by Supabase: open one, hit "Copy HTML for Supabase", and
        paste it into the Supabase dashboard to bring it onto the brand.
      </Text>

      <div className="admin__email-tpl">
        <nav className="admin__email-tpl-list" aria-label="Email templates">
          {GROUPS.map((g) => (
            <div key={g.group} className="admin__email-tpl-group">
              <p className="admin__email-tpl-grouptitle">{g.group}</p>
              {g.items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  className={`admin__email-tpl-item ${selected === it.id ? 'admin__email-tpl-item--on' : ''}`}
                  onClick={() => setSelected(it.id)}
                  aria-pressed={selected === it.id}
                >
                  {it.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="admin__email-tpl-main">
          <div className="admin__email-tpl-bar">
            <div className="admin__email-tpl-subject">
              <span className="admin__email-tpl-subject-label">Subject</span>
              <span className="admin__email-tpl-subject-text">{subject || '…'}</span>
            </div>
            <div className="admin__subnav admin__email-tpl-device">
              <button
                type="button"
                className={`admin__subnav-tab ${device === 'desktop' ? 'admin__subnav-tab--active' : ''}`}
                onClick={() => setDevice('desktop')}
              >
                Desktop
              </button>
              <button
                type="button"
                className={`admin__subnav-tab ${device === 'mobile' ? 'admin__subnav-tab--active' : ''}`}
                onClick={() => setDevice('mobile')}
              >
                Mobile
              </button>
            </div>
          </div>

          {current?.auth && (
            <div className="admin__email-tpl-supabase">
              <Button type="button" variant="secondary" size="sm" onClick={copyForSupabase} disabled={copying}>
                {copying ? '…' : 'Copy HTML for Supabase'}
              </Button>
              <Text role="caption" muted>
                Paste into Supabase -&gt; Authentication -&gt; Email Templates -&gt; <strong>{current.supabase}</strong>.
                The sign-in links use the GoTrue variables, so they stay live.
              </Text>
            </div>
          )}

          {error ? (
            <div className="admin__error"><p className="kicker">Problem</p><p>{error}</p></div>
          ) : (
            <div className="admin__email-tpl-frame-wrap">
              <iframe
                title="Email preview"
                className={`admin__email-tpl-frame admin__email-tpl-frame--${device}`}
                srcDoc={html}
                sandbox=""
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
